/**
 * Pardot integration utilities
 */

/** Marketing form handler for the Geoscape Verify "coming soon" waitlist. A fixed
 *  marketing endpoint, the same across environments, so it lives here rather than in env. */
const VERIFY_WAITLIST_FORM_HANDLER_URL =
  "https://go.geoscape.com.au/l/520681/2026-09-07/gh8tcs";

export interface VerifyWaitlistData {
  email: string;
  /** Cognito sub — the form handler's DoD User ID field. */
  dodUserId?: string;
  organisationId?: string;
  /** Module labels the user wants to hear about; sent as one repeated field each. */
  verifyModules: string[];
}

/**
 * Registers interest in Geoscape Verify modules with Pardot.
 *
 * Unlike {@link sendToPardotFormHandler} this rethrows on network failure so the
 * caller can surface success or failure — with `no-cors` the response is opaque,
 * so a resolved fetch is the only signal that the submission was dispatched.
 */
export const sendVerifyWaitlist = async (
  data: VerifyWaitlistData,
): Promise<void> => {
  const formData = new URLSearchParams();
  formData.append("email", data.email);

  if (data.dodUserId) {
    formData.append("dodUserId", data.dodUserId);
  }

  if (data.organisationId) {
    formData.append("organisationId", data.organisationId);
  }

  for (const module of data.verifyModules) {
    formData.append("verifyModules", module);
  }

  await fetch(VERIFY_WAITLIST_FORM_HANDLER_URL, {
    method: "POST",
    body: formData,
    mode: "no-cors", // Pardot form handlers typically don't support CORS
  });
};

export interface PardotFormData {
  email: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
  organisationId?: string;
}

/**
 * Sends user data to Pardot form handler using fetch (POST)
 * @param formHandlerUrl The Pardot form handler URL (from environment variables)
 * @param data The form data to send
 */
export const sendToPardotFormHandler = async (
  formHandlerUrl: string | undefined,
  data: PardotFormData,
): Promise<void> => {
  if (!formHandlerUrl) {
    console.warn("Pardot form handler URL not configured");
    return;
  }

  try {
    const formData = new URLSearchParams();
    formData.append("email", data.email);

    if (data.firstName) {
      formData.append("firstName", data.firstName);
    }

    if (data.lastName) {
      formData.append("lastName", data.lastName);
    }

    if (data.userId) {
      formData.append("dodUserId", data.userId);
    }

    if (data.organisationId) {
      formData.append("dodOrganisationId", data.organisationId);
    }

    await fetch(formHandlerUrl, {
      method: "POST",
      body: formData,
      mode: "no-cors", // Pardot form handlers typically don't support CORS
    });
  } catch (error) {
    console.error("Failed to send data to Pardot:", error);
    // Don't throw - we don't want to fail the primary operation if Pardot tracking fails
  }
};

/**
 * Builds a URL with query parameters for Pardot form handler (for iframe approach)
 * @param formHandlerUrl The Pardot form handler URL (from environment variables)
 * @param data The form data to send
 * @returns The complete URL with query parameters for use in iframe src
 */
export const buildPardotIframeUrl = (
  formHandlerUrl: string | undefined,
  data: PardotFormData,
): string | null => {
  if (!formHandlerUrl) {
    console.warn("Pardot form handler URL not configured");
    return null;
  }

  try {
    const params = new URLSearchParams();
    params.append("email", data.email);

    if (data.firstName) {
      params.append("firstName", data.firstName);
    }

    if (data.lastName) {
      params.append("lastName", data.lastName);
    }

    if (data.userId) {
      params.append("dodUserId", data.userId);
    }

    if (data.organisationId) {
      params.append("dodOrganisationId", data.organisationId);
    }

    return `${formHandlerUrl}?${params.toString()}`;
  } catch (error) {
    console.error("Failed to build Pardot iframe URL:", error);
    return null;
  }
};
